import React from 'react';
import { Filler } from 'chart.js';
import { Line, Chart as ChartJS, Bar } from 'react-chartjs-2';
import { Button, Pane } from 'evergreen-ui';
import 'chartjs-adapter-date-fns';
import SuspenseSpinner from 'Utils/SuspenseSpinner';
import annotationPlugin from 'chartjs-plugin-annotation';
import zoomPlugin from 'chartjs-plugin-zoom';
import { pluginCrossLine } from './fromPhoebe/plugin.crossline';
import { chartjsPluginProvideGroup } from './fromPhoebe/plugin.provideGroup';
import { createZoomAnnotations, getZoomPluginOptions } from './zoom';

ChartJS.register(Filler);
ChartJS.register(annotationPlugin, zoomPlugin);

function ChartDefault({
  datasets = [],
  chartTitle,
  chartOptions = { width: 0, height: 0, confines: [] },
  loadOnClick = false,
  loadOnClickOptions = { onClick: null, isLoading: false, isSuccess: false },
  scales = { x: {} },
  groupKey = 'main',
  doSyncZoom = true,
  minZoomRange,
  disableZoom = false,
  type = 'line',
  plugins: outerPlugins = [],
  timeFormatPostfix = '',
}, outerRef) {
  const {
    onClick,
    isSuccess,
    isLoading,
  } = loadOnClickOptions;
  const {
    width,
    height,
    minDateMS,
    maxDateMS,
    confines,
  } = chartOptions;
  const chartRef = React.useRef();
  React.useImperativeHandle(outerRef, () => chartRef.current);

  const confinesAnnotations = {};
  if (confines.length > 0 && datasets.length > 0) {
    const confinesNameByValue = {};

    confines.forEach((c) => {
      if (confinesNameByValue[c.value]) confinesNameByValue[c.value] = `${confinesNameByValue[c.value]}, ${c.label}`;
      else confinesNameByValue[c.value] = c.label;
    });

    Object.keys(confinesNameByValue).forEach((value, i) => {
      confinesAnnotations[`annotation${i}`] = {
        type: 'line',
        borderColor: 'rgb(100, 149, 237)',
        borderDash: [6, 6],
        borderDashOffset: 0,
        borderWidth: 1,
        label: {
          enabled: true,
          content: confinesNameByValue[value],
          position: 'start',
          borderRadius: 2,
          padding: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.066)',
          color: 'black',
          font: {
            size: 9,
          },
        },
        scaleID: 'y',
        value: Number(value),
      };
    });
  }

  let tooltipFormat = 'dd.MM.yyyy HH:mm';

  if (timeFormatPostfix) {
    tooltipFormat += ` '${timeFormatPostfix}'`;
  }

  const baseXScaleOptions = {
    type: 'time',
    min: minDateMS,
    max: maxDateMS,
    ticks: {
      minRotation: 0,
      maxRotation: 0,
    },
    time: {
      tooltipFormat,
      minUnit: 'minute',
      displayFormats: {
        month: 'MM.yyyy',
        day: 'dd.MM.yyyy',
        hour: ' dd (HH:mm) ',
        minute: 'HH:mm',
      },
    },
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    normalized: true,
    spanGaps: true,
    parsing: false,
    plugins: {
      annotation: {
        annotations: {
          ...confinesAnnotations,
          ...createZoomAnnotations(doSyncZoom),
        },
      },
      zoom: getZoomPluginOptions(doSyncZoom, minZoomRange, disableZoom),
      legend: {
        position: 'top',
      },
      title: {
        display: !!chartTitle,
        text: chartTitle || '',
      },
      decimation: {
        enabled: true,
        algorithm: 'min-max',
        // algorithm: 'lttb', // more aggressive mode
      },
      provideGroup: { // настройка плагина из phoebe
        groupKey,
        chartKey: chartTitle, // TODO: тут бы нормальный ключ сделать
      },
      crossLine: { // настройка плагина из phoebe
        snap: true,
      },
    },
    // parsing: {
    //   xAxisKey: 'timeUTC',
    //   yAxisKey: 'value',
    // },
    interaction: {
      mode: 'nearest',
      intersect: false,
      axis: 'x',
    },
    scales: Object.fromEntries(
      Object.entries(scales).map(
        ([key, value]) => (
          key.startsWith('x')
            ? [key, {
              ...baseXScaleOptions,
              ...value,
              time: {
                ...baseXScaleOptions.time,
                ...value.time,
              },
            }]
            : [key, value]
        ),
      ),
    ),
  };

  const chartData = React.useMemo(() => (datasets.map((dataset) => ({
    fill: false, // заливка фона по значениям
    lineTension: 0.1, // 0 - ломаная линия, выше - скругленная,
    // pointBorderWidth: 1,
    borderWidth: 1.5,
    pointRadius: (ctx) => {
      if (ctx?.raw?.isPoint === true) return 1.5;
      return 0;
    },
    segment: {
      borderColor: ({ p0, p1 }) => {
        if (p0?.raw?.isPoint === true || p1?.raw?.isPoint === true) return 'rgba(0, 0, 0, 0)';
        return undefined;
      },
    },
    ...dataset,
    data: dataset.data.map((d, i) => {
      if (!d) {
        return null;
      }

      const result = {
        x: new Date(d.timeUTC).getTime(),
        y: Number(d.value),
      };

      if (dataset.data[i + 1] === null || dataset.data[i - 1] === null) {
        result.isPoint = true;
      }

      return result;
    }).filter(Boolean),
  }))), [datasets]);

  const plugins = [pluginCrossLine];

  if (groupKey) {
    plugins.push(chartjsPluginProvideGroup);
  }

  if (outerPlugins) {
    plugins.push(...outerPlugins);
  }

  // React.useEffect(() => {
  //   chartRef.current.resize(width, height);
  // }, [chartRef, width, height]);

  // console.group('Chart.js');
  // console.log('data', chartData);
  // console.log('options', options);
  // console.log(chartRef.current);
  // console.groupEnd();

  return (
    <Pane
      position="relative"
      flex={1}
      className="print-break"
      overflow="hidden"
      backgroundColor={loadOnClick && !isSuccess ? 'rgba(0, 0, 0, 0.25)' : 'transparent'}
    >
      {loadOnClick && !isSuccess && (
      <Pane
        position="absolute"
        top={30}
        right={0}
        bottom={0}
        left={0}
        display="grid"
        alignItems="center"
        className="print-exclude"
      >
        {!isLoading && <Button margin="auto" onClick={onClick}>Загрузить данные</Button>}
        {isLoading && <SuspenseSpinner />}
      </Pane>
      )}
      <Pane height={height} width={width}>
        <ChartByType
          type={type}
          chartRef={chartRef}
          chartData={chartData}
          options={options}
          plugins={plugins}
        />
      </Pane>
    </Pane>
  );
}

const Chart = React.forwardRef(ChartDefault);
Chart.displayName = 'Chart';

function ChartByType({ type, chartRef, chartData, options, plugins }) {
  const props = {
    ref: chartRef,
    data: { datasets: chartData },
    options,
    plugins,
  };

  if (type === 'line') return <Line {...props} />;
  if (type === 'bar') return <Bar {...props} />;
  return null;
}

export default Chart;
